import React, { useState, useRef, useEffect, useMemo, useCallback, ReactNode } from 'react';
import { getVideoStreamUrl } from '../../services/videoApi';
import { Video, VideoPlayerError, UseVideoEditBasicInfoSectionReturn } from './types';
import logger from '../../utils/logger';

/**
 * URLを検出してリンクに変換する関数
 */
const convertUrlsToLinks = (text: string | undefined): ReactNode => {
  if (!text) return '';
  
  // URLを検出する正規表現
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  
  // テキストをURLで分割
  const parts: Array<{ type: 'text' | 'url'; content: string }> = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  
  // 正規表現で検索しながらテキストを分割
  while ((match = urlRegex.exec(text)) !== null) {
    // URLの前の通常テキスト
    if (match.index > lastIndex) {
      parts.push({
        type: 'text',
        content: text.substring(lastIndex, match.index)
      });
    }
    
    // URL部分
    parts.push({
      type: 'url',
      content: match[0]
    });
    
    lastIndex = match.index + match[0].length;
  }
  
  // 最後の部分を追加
  if (lastIndex < text.length) {
    parts.push({
      type: 'text',
      content: text.substring(lastIndex)
    });
  }
  
  // パーツを適切なReact要素に変換
  return parts.map((part, index) => {
    if (part.type === 'text') {
      return <span key={`text-${index}`}>{part.content}</span>;
    } else {
      return (
        <a 
          key={`link-${index}`} 
          href={part.content} 
          target="_blank" 
          rel="noopener noreferrer"
          style={{ 
            color: '#3b82f6', 
            textDecoration: 'underline', 
            wordBreak: 'break-all' 
          }}
        >
          {part.content}
        </a>
      );
    }
  });
};

/**
 * VideoEditBasicInfoSection コンポーネントのビジネスロジックを管理するカスタムフック
 */
export const useVideoEditBasicInfoSection = (video: Video | null): UseVideoEditBasicInfoSectionReturn => {
  const [isPortrait, setIsPortrait] = useState<boolean>(false);
  const [error, setError] = useState<VideoPlayerError | null>(null);
  const [usingFallback, setUsingFallback] = useState<boolean>(false);
  const playerRef = useRef<any>(null);
  const [containerWidth, setContainerWidth] = useState<string>('100%');
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [loadRetries, setLoadRetries] = useState<number>(0);
  const maxRetries = 2; // 最大再試行回数
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth <= 768);
  const [showPreview, setShowPreview] = useState<boolean>(false); // プレビュー表示の状態を管理

  // レスポンシブ対応のための副作用
  useEffect(() => {
    const handleResize = (): void => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  /**
   * パスの正規化（すべての形式のパスを処理）
   */
  const getNormalizedPath = useCallback((path: string | undefined): string | null => {
    if (!path) return null;
    
    // すでに完全なURLの場合、そのまま返す
    if (path.startsWith('http://') || path.startsWith('https://')) {
      // 空白文字をエンコードして安全に扱えるようにする
      return path.replace(/ /g, '%20');
    }

    // http(s):// 以外はすべて先頭スラッシュ1個に正規化して相対URLとして返す
    // （先頭スラッシュ有り・無し両パターンを正規化し、//uploads や uploads/ の事故を防ぐ）
    return '/' + path.replace(/^\/+/, '').replace(/ /g, '%20');
  }, []);

  /**
   * 実際に使用する動画URL
   * - ストリーミングが可能かつ usingFallback が false ならストリーミングURL
   * - それ以外なら通常ファイルパス
   */
  // 動画URLをメモ化
  const memoizedVideoUrl = useMemo(() => {
    if (!video) return null;
    
    return (!usingFallback && video.id)
      ? getVideoStreamUrl(video.id)
      : getNormalizedPath(video.path);
  }, [video?.id, video?.path, usingFallback]);

  // 初期値の設定
  useEffect(() => {
    if (memoizedVideoUrl) {
      // 現在のURLと異なる場合のみ更新
      setVideoUrl(prevUrl => {
        if (prevUrl !== memoizedVideoUrl) {
          logger.devLog('動画URL設定:', memoizedVideoUrl);
          return memoizedVideoUrl;
        }
        return prevUrl;
      });
    }
  }, [memoizedVideoUrl]);

  /**
   * URLのログを確認するためのuseEffect
   */
  useEffect(() => {
    if (!video) return;    
    logger.devLog('VideoEdit - URL設定:', {
      videoId: video?.id,
      videoPath: video?.path,
      usingFallback,
      finalUrl: videoUrl,
      memoizedUrl: memoizedVideoUrl,
    });
  }, [video?.id, video?.path, videoUrl, usingFallback, memoizedVideoUrl]);

  /**
   * ResizeObserver関連のエラーを抑制するための対策
   */
  useEffect(() => {
    const resizeObserverError = (err: Event): void => {
      // エラーオブジェクトとそのmessageプロパティが存在するか確認
      if (
        err && 
        err instanceof ErrorEvent && 
        err.message && 
        (err.message.includes('ResizeObserver') ||
        err.message.includes('ResizeObserver loop completed with undelivered notifications'))
      ) {
        // エラーをキャンセルして、コンソールにはログだけ残す
        err.stopImmediatePropagation();
      }
    };
    
    window.addEventListener('error', resizeObserverError, true);
    
    return () => {
      window.removeEventListener('error', resizeObserverError, true);
    };
  }, []);

  /**
   * 初期表示時にコンテナの幅を設定
   * （画面幅に応じて 800px を上限としつつ 95%を使用）
   */
  useEffect(() => {
    const viewportWidth = window.innerWidth;
    const maxPlayerWidth = Math.min(viewportWidth * 0.95, 800);
    setContainerWidth(`${maxPlayerWidth}px`);
  }, []);

  /**
   * 動画の準備ができた際に呼ばれる
   */
  const handleVideoReady = (player: any): void => {
    // 動画の向きを判定
    if (player && player.getInternalPlayer()) {
      const videoElement = player.getInternalPlayer();
      if (videoElement.videoWidth && videoElement.videoHeight) {
        setIsPortrait(videoElement.videoHeight > videoElement.videoWidth);
      }
    }
  };

  /**
   * エラーハンドリング（ストリーミングエラー -> フォールバック）
   */
  const handleError = (e: any): void => {
    console.error('ビデオプレイヤーエラー:', e);
    
    // エラーが認証関連かどうかを判断
    const isAuthError = e && (
      (typeof e === 'string' && e.includes('403')) ||
      (e.target && e.target.error && e.target.error.code === 4) || // MEDIA_ERR_SRC_NOT_SUPPORTED
      (e.response && e.response.status === 403)
    );
    
    if (isAuthError) {
      if (loadRetries < maxRetries) {
        // 再試行回数を増やして再ロード
        setLoadRetries(prev => prev + 1);
        setTimeout(() => {
          // フォールバックとして別のURLを試す
          if (!usingFallback && video && video.file_path) {
            // 先頭スラッシュ正規化のうえ相対URLとして設定（//uploads や uploads/ の事故を防ぐ）
            setVideoUrl('/' + video.file_path.replace(/^\/+/, ''));
            setUsingFallback(true);
          }
        }, 1000);
      } else {
        setError(e);
        // 最大再試行回数を超えた場合はエラーを表示
        console.error('最大再試行回数を超えて動画再生に失敗しました。ファイルパスを確認してください:', video?.file_path);
      }
    } else {
      // 認証以外のエラーの場合はすぐにエラー表示
      setError(e);
    }
  };

  // 日付を「yyyy/mm/dd」形式でフォーマットする関数
  const formatDate = (date: string | null): string => {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}/${month}/${day}`;
  };

  // スタイル定義
  const styles = useMemo(() => {
    return {
      playerWrapper: {
        width: containerWidth,
        maxWidth: '100%',
      }
    };
  }, [containerWidth]);

  // 動画プレーヤーをレンダリングする関数
  const renderVideoPlayer = (): ReactNode => {
    if (!video || !video.file_path || !videoUrl) return null;
    
    return null; // 実際の実装はプレゼンテーションコンポーネントで行うため、ここでは空を返す
  };

  return {
    isPortrait,
    error,
    usingFallback,
    playerRef,
    containerWidth,
    videoUrl,
    loadRetries,
    isMobile,
    showPreview,
    setShowPreview,
    handleVideoReady,
    handleError,
    formatDate,
    convertUrlsToLinks,
    renderVideoPlayer,
    styles
  };
};

export { convertUrlsToLinks }; 
